import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600',
        secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
        outline: 'border border-gray-200 bg-transparent hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-900',
        ghost: 'hover:bg-gray-100 dark:hover:bg-gray-800',
        danger: 'bg-error-500 text-white shadow-theme-xs hover:bg-error-600',
        success: 'bg-success-500 text-white shadow-theme-xs hover:bg-success-600',
      },
      size: {
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-3 text-sm',
        lg: 'px-5 py-3.5 text-sm',
        xl: 'px-6 py-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  pressed?: boolean;
  iconOnlyOnMobile?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, pressed, iconOnlyOnMobile, children, ...props }, ref) => {
    const renderChildren = () => {
      if (!iconOnlyOnMobile) return children;

      const childArray = React.Children.toArray(children);
      return childArray.map((child, index) => {
        if (React.isValidElement(child)) {
          return <React.Fragment key={index}>{child}</React.Fragment>;
        }
        if (typeof child === 'string' || typeof child === 'number') {
          return (
            <span key={index} className="max-sm:hidden">
              {child}
            </span>
          );
        }
        return child;
      });
    };

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-pressed={pressed !== undefined ? pressed : undefined}
        {...props}
      >
        {renderChildren()}
      </button>
    );
  }
);

Button.displayName = 'Button';
